import { useLocation, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { useDashboardContext } from "@/components/dashboard/DashboardContext";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function WorkspaceSwitcher() {
	const { workspace, workspaces, switchWorkspace, isWorkspacesLoading } =
		useDashboardContext();
	const navigate = useNavigate();
	const location = useLocation();
	const [isSwitching, setIsSwitching] = useState(false);

	if (isWorkspacesLoading || !workspace) return null;

	const handleSwitch = async (workspaceId: string) => {
		if (workspaceId === workspace.id || isSwitching) return;
		const nextWorkspace = workspaces.find((ws) => ws.id === workspaceId);
		if (!nextWorkspace) return;

		setIsSwitching(true);
		try {
			await switchWorkspace(workspaceId);

			// Keep the settings route slug in sync with the newly active workspace.
			if (
				location.pathname.startsWith("/dashboard/workspaces/") &&
				location.pathname.endsWith("/settings")
			) {
				navigate({
					to: "/dashboard/workspaces/$workspaceSlug/settings",
					params: { workspaceSlug: nextWorkspace.slug },
				});
			}

			toast.success(`Switched to ${nextWorkspace.name}`);
		} catch {
			toast.error("Failed to switch workspace");
		} finally {
			setIsSwitching(false);
		}
	};

	const handleCreateWorkspace = () => {
		navigate({ to: "/dashboard/new-workspace" as string });
	};

	return (
		<DropdownMenu>
			<DropdownMenuTrigger
				className="flex items-center gap-1.5 px-2 py-1.5 text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
				style={{ color: "#ffffff" }}
				disabled={isSwitching}
				onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = "#201f1f"; }}
				onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = "transparent"; }}
				onFocus={e => { (e.currentTarget as HTMLElement).style.backgroundColor = "#201f1f"; }}
				onBlur={e => { (e.currentTarget as HTMLElement).style.backgroundColor = "transparent"; }}
			>
				<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "14px", color: "#888888" }}>corporate_fare</span>
				<span className="max-w-[120px] truncate">{workspace.name}</span>
				<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "12px", color: "#888888" }}>unfold_more</span>
			</DropdownMenuTrigger>
			<DropdownMenuContent
				align="start"
				className="w-56"
				style={{ backgroundColor: "#111111", border: "1px solid rgba(255,255,255,0.08)" }}
			>
				{workspaces.map((ws) => (
					<DropdownMenuItem
						key={ws.id}
						onClick={() => handleSwitch(ws.id)}
						className="flex items-center justify-between"
						style={{ color: "#ffffff" }}
					>
						<span className="truncate">{ws.name}</span>
						{ws.id === workspace.id && (
							<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "14px", color: "#6B3FF2", fontVariationSettings: "'FILL' 1" }}>check</span>
						)}
					</DropdownMenuItem>
				))}
				<DropdownMenuSeparator />
				<DropdownMenuItem onClick={handleCreateWorkspace}>
					<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "14px" }}>add</span>
					Create workspace
				</DropdownMenuItem>
				<DropdownMenuItem
					onClick={() =>
						navigate({
							to: "/dashboard/workspaces/$workspaceSlug/settings",
							params: { workspaceSlug: workspace.slug },
						})
					}
				>
					<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "14px" }}>settings</span>
					Workspace settings
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
